import React, { useState, useEffect } from "react";
import { db } from "../firebase";
import {
  collection,
  addDoc,
  onSnapshot,
  query,
  orderBy,
  serverTimestamp,
  deleteDoc,
  updateDoc,
  doc,
} from "firebase/firestore";
import {
  Coins,
  Edit2,
  XCircle,
  Calculator,
  CheckCircle2,
  Trash2,
  TrendingUp, // 新增一個 icon 給匯率用
} from "lucide-react";

const ALL_USERS = ["Emily", "Candy", "Max", "Amy", "Cindy"];

export default function ExpenseTracker({ currentUser, onCostUpdate }) {
  const [activeTab, setActiveTab] = useState("record");

  const [amount, setAmount] = useState("");
  // ✅ 修改 1: 新增匯率狀態，預設 0.2
  const [exchangeRate, setExchangeRate] = useState(0.2);
  const [item, setItem] = useState("");
  const [paidBy, setPaidBy] = useState(currentUser);
  const [splitWith, setSplitWith] = useState(ALL_USERS);

  const [editingId, setEditingId] = useState(null);
  const [expenses, setExpenses] = useState([]);

  useEffect(() => {
    const q = query(collection(db, "expenses"), orderBy("createdAt", "desc"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const expensesData = snapshot.docs.map((doc) => {
        const data = doc.data();
        let finalSplit = data.splitWith;
        if (!finalSplit) {
          finalSplit = data.isPublic ? ALL_USERS : [data.paidBy];
        }
        return { id: doc.id, ...data, splitWith: finalSplit };
      });
      setExpenses(expensesData);
    });
    return () => unsubscribe();
  }, []);

  // ✅ 修改 2: 個人總花費也要改成「台幣」計算 (若你想看台幣總額)
  const myTotalCost = expenses.reduce((total, exp) => {
    if (exp.splitWith && exp.splitWith.includes(currentUser)) {
      // 如果這筆資料沒有匯率(舊資料)，就用預設 0.2
      const currentRate = exp.exchangeRate || 0.2;
      // 先轉成台幣：(日幣 / 人數) * 匯率
      const costPerPersonTWD =
        (exp.amount / exp.splitWith.length) * currentRate;
      return total + costPerPersonTWD;
    }
    return total;
  }, 0);

  useEffect(() => {
    if (onCostUpdate) {
      onCostUpdate(Math.round(myTotalCost));
    }
  }, [myTotalCost, onCostUpdate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!amount || !item || splitWith.length === 0) return;
    try {
      const docData = {
        item,
        amount: Number(amount),
        // ✅ 修改 3: 儲存匯率，確保是數字
        exchangeRate: Number(exchangeRate) || 0.2,
        paidBy,
        splitWith,
        isPublic: splitWith.length > 1,
      };
      if (editingId) {
        await updateDoc(doc(db, "expenses", editingId), docData);
        alert("修改成功！");
        cancelEdit();
      } else {
        await addDoc(collection(db, "expenses"), {
          ...docData,
          createdAt: serverTimestamp(),
        });
      }
      setAmount("");
      setItem("");
      // ✅ 修改 4: 重置時設回預設值
      setExchangeRate(0.2);
      setSplitWith(ALL_USERS);
      setPaidBy(currentUser);
    } catch (error) {
      console.error(error);
      alert("操作失敗");
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("刪除？")) {
      await deleteDoc(doc(db, "expenses", id));
      if (editingId === id) cancelEdit();
    }
  };

  const startEdit = (exp) => {
    setEditingId(exp.id);
    setItem(exp.item);
    setAmount(exp.amount);
    // ✅ 修改 5: 編輯時帶入該筆的匯率，沒有就帶 0.2
    setExchangeRate(exp.exchangeRate || 0.2);
    setPaidBy(exp.paidBy);
    setSplitWith(exp.splitWith || ALL_USERS);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setAmount("");
    setItem("");
    setExchangeRate(0.2); // 重置
    setSplitWith(ALL_USERS);
    setPaidBy(currentUser);
  };

  // ... (toggleSplitUser, toggleAllSplit 保持不變) ...
  const toggleSplitUser = (user) => {
    if (splitWith.includes(user)) {
      if (splitWith.length > 1)
        setSplitWith(splitWith.filter((u) => u !== user));
    } else setSplitWith([...splitWith, user]);
  };
  const toggleAllSplit = () => {
    setSplitWith(splitWith.length === ALL_USERS.length ? [paidBy] : ALL_USERS);
  };

  return (
    <div className="space-y-6 pb-20 pt-4">
      {/* 頁籤切換 */}
      <div className="flex bg-white p-1 rounded-2xl shadow-sm border border-gold/20 mx-4">
        <button
          onClick={() => setActiveTab("record")}
          className={`flex-1 py-2 rounded-xl text-sm font-bold transition-all flex items-center justify-center gap-2 ${activeTab === "record" ? "bg-starbucks text-white shadow-md" : "text-gray-400 hover:text-gray-600"}`}
        >
          <Edit2 size={16} /> 記帳 (JPY)
        </button>
        <button
          onClick={() => setActiveTab("settle")}
          className={`flex-1 py-2 rounded-xl text-sm font-bold transition-all flex items-center justify-center gap-2 ${activeTab === "settle" ? "bg-starbucks text-white shadow-md" : "text-gray-400 hover:text-gray-600"}`}
        >
          <Calculator size={16} /> 結算 (TWD)
        </button>
      </div>

      {activeTab === "record" && (
        <>
          <div
            className={`mx-4 p-6 rounded-3xl shadow-warm border relative overflow-hidden transition-all ${editingId ? "bg-orange-50 border-orange-200" : "bg-white border-gold/20"}`}
          >
            {/* ... (標題部分保持不變) ... */}
            <div className="flex justify-between items-center mb-6">
              <h2
                className={`text-xl font-bold flex items-center gap-2 font-serif ${editingId ? "text-orange-600" : "text-starbucks-dark"}`}
              >
                {editingId ? (
                  <>
                    <Edit2 size={20} /> 修改消費
                  </>
                ) : (
                  <>
                    <Coins className="text-gold" /> 新增消費
                  </>
                )}
              </h2>
              {editingId && (
                <button
                  onClick={cancelEdit}
                  className="text-xs flex items-center gap-1 text-gray-500 bg-white px-2 py-1 rounded-full border border-gray-200"
                >
                  <XCircle size={14} /> 取消
                </button>
              )}
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="text-xs font-bold text-hojicha uppercase tracking-wider block mb-2 ml-1">
                  品項
                </label>
                <input
                  type="text"
                  value={item}
                  onChange={(e) => setItem(e.target.value)}
                  className="w-full bg-warm-paper border-2 border-transparent rounded-2xl p-4 text-text-primary focus:outline-none focus:border-starbucks/50 focus:bg-white transition-all font-medium placeholder-hojicha/40"
                  placeholder="例如: 章魚燒..."
                />
              </div>

              {/* ✅ 修改 6: 金額與匯率排在同一行 */}
              <div className="flex gap-3">
                <div className="flex-[2]">
                  {" "}
                  {/* 金額欄位佔寬一點 */}
                  <label className="text-xs font-bold text-hojicha uppercase tracking-wider block mb-2 ml-1">
                    金額 (JPY)
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-4 text-hojicha font-bold">
                      ¥
                    </span>
                    <input
                      type="number"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      className="w-full bg-warm-paper border-2 border-transparent rounded-2xl p-4 pl-8 text-text-primary focus:outline-none focus:border-starbucks/50 focus:bg-white transition-all font-mono font-bold text-lg"
                      placeholder="0"
                    />
                  </div>
                </div>

                <div className="flex-1">
                  {" "}
                  {/* 匯率欄位 */}
                  <label className="text-xs font-bold text-hojicha uppercase tracking-wider block mb-2 ml-1">
                    匯率
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      step="0.000001" // 允許小數點
                      value={exchangeRate}
                      onChange={(e) => setExchangeRate(e.target.value)}
                      className="w-full bg-warm-paper border-2 border-transparent rounded-2xl p-4 text-center text-text-primary focus:outline-none focus:border-starbucks/50 focus:bg-white transition-all font-mono font-bold text-lg"
                      placeholder="0.2"
                    />
                  </div>
                </div>
              </div>

              {/* 即時預覽換算結果 (選用功能) */}
              {amount && (
                <div className="text-right text-xs text-gray-400 font-mono -mt-3 mr-1">
                  ≈ NT$ {Math.round(amount * (exchangeRate || 0.2))}
                </div>
              )}

              <div className="w-full">
                <label className="text-xs font-bold text-hojicha uppercase tracking-wider block mb-2 ml-1">
                  先付
                </label>
                <select
                  value={paidBy}
                  onChange={(e) => setPaidBy(e.target.value)}
                  className="w-full h-[60px] bg-warm-paper border-2 border-transparent rounded-2xl px-2 text-starbucks-dark font-bold focus:outline-none focus:border-starbucks/50 text-sm"
                >
                  {ALL_USERS.map((u) => (
                    <option key={u} value={u}>
                      {u}
                    </option>
                  ))}
                </select>
              </div>

              {/* ... (分給誰的部分保持不變) ... */}
              <div>
                <div className="flex justify-between items-center mb-2 ml-1">
                  <label className="text-xs font-bold text-hojicha uppercase tracking-wider">
                    分給誰? ({splitWith.length})
                  </label>
                  <button
                    type="button"
                    onClick={toggleAllSplit}
                    className="text-[10px] text-starbucks font-bold px-2 py-1 bg-starbucks/10 rounded-md"
                  >
                    {splitWith.length === ALL_USERS.length ? "取消" : "全選"}
                  </button>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {ALL_USERS.map((user) => (
                    <button
                      key={user}
                      type="button"
                      onClick={() => toggleSplitUser(user)}
                      className={`py-2 rounded-xl text-xs font-bold transition-all border-2 text-center truncate ${splitWith.includes(user) ? "bg-starbucks text-white border-starbucks shadow-md" : "bg-white text-gray-400 border-gray-100"}`}
                    >
                      {user}
                    </button>
                  ))}
                </div>
              </div>

              <button
                type="submit"
                className={`w-full text-white font-bold py-4 rounded-2xl shadow-lg transition-all flex items-center justify-center gap-2 text-lg font-serif tracking-widest mt-2 ${editingId ? "bg-orange-500" : "bg-gradient-to-r from-starbucks to-[#005c3f]"}`}
              >
                {editingId ? "確認修改" : "確認記帳"}
              </button>
            </form>
          </div>

          <div className="mx-4 space-y-3">
            {/* 列表顯示 */}
            {expenses.map((expense) => (
              <div
                key={expense.id}
                className={`bg-white p-4 rounded-2xl flex flex-col gap-2 border shadow-sm transition-all ${editingId === expense.id ? "border-orange-400 ring-2 ring-orange-100" : "border-hojicha-light/20"}`}
              >
                <div className="flex justify-between items-start">
                  <div className="flex items-center gap-3">
                    {/* ... (頭像保持不變) ... */}
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 font-serif font-bold text-white shadow-sm ${expense.splitWith.length === 1 ? "bg-gray-400" : "bg-starbucks"}`}
                    >
                      {expense.paidBy.charAt(0)}
                    </div>
                    <div>
                      <p className="font-bold text-text-primary text-base">
                        {expense.item}
                      </p>
                      <p className="text-xs text-hojicha mt-0.5 font-medium flex items-center gap-1 flex-wrap">
                        <span className="text-starbucks-dark font-bold">
                          {expense.paidBy}
                        </span>{" "}
                        先付 •
                        <span className="text-gray-400">
                          匯率 {expense.exchangeRate || 0.2}
                        </span>
                      </p>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <span className="block font-mono font-bold text-lg text-starbucks-dark">
                      ¥{expense.amount.toLocaleString()}
                    </span>
                    {/* ✅ 修改 7: 列表顯示約略台幣 */}
                    <span className="block text-xs text-gray-400 font-mono">
                      ≈ NT$
                      {Math.round(
                        expense.amount * (expense.exchangeRate || 0.2),
                      )}
                    </span>
                    <div className="flex justify-end gap-2 mt-1">
                      <button
                        onClick={() => startEdit(expense)}
                        className="text-gray-300 hover:text-orange-500"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={() => handleDelete(expense.id)}
                        className="text-gray-300 hover:text-red-500"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
      {activeTab === "settle" && <SettlementView expenses={expenses} />}
    </div>
  );
}

// ✅ 修改 8: 修改結算視圖邏輯
function SettlementView({ expenses }) {
  const calculateBalances = () => {
    const balances = {};
    const details = {};
    ALL_USERS.forEach((u) => {
      balances[u] = 0;
      details[u] = { paid: 0, shouldPay: 0 };
    });

    expenses.forEach((exp) => {
      // ✅ 核心修改：每一筆都先換算成台幣
      const rate = exp.exchangeRate || 0.2; // 如果是舊資料沒有匯率，預設 0.2
      const amountTWD = exp.amount * rate; // 這邊算出這一筆的「總台幣」

      const payer = exp.paidBy;
      const consumers = exp.splitWith || (exp.isPublic ? ALL_USERS : [payer]);

      if (consumers.length > 0) {
        const costPerPersonTWD = amountTWD / consumers.length;

        if (balances[payer] !== undefined) {
          balances[payer] += amountTWD;
          details[payer].paid += amountTWD;
        }
        consumers.forEach((consumer) => {
          if (balances[consumer] !== undefined) {
            balances[consumer] -= costPerPersonTWD;
            details[consumer].shouldPay += costPerPersonTWD;
          }
        });
      }
    });
    return { balances, details };
  };

  const { balances, details } = calculateBalances();

  // ... (getTransactions 邏輯保持不變，因為 balances 已經是台幣了) ...
  const getTransactions = () => {
    let debtors = [];
    let creditors = [];
    Object.entries(balances).forEach(([user, amount]) => {
      if (amount < -1) debtors.push({ user, amount });
      if (amount > 1) creditors.push({ user, amount });
    });
    debtors.sort((a, b) => a.amount - b.amount);
    creditors.sort((a, b) => b.amount - a.amount);
    const transactions = [];
    let i = 0,
      j = 0;
    while (i < debtors.length && j < creditors.length) {
      let debtor = debtors[i];
      let creditor = creditors[j];
      let amount = Math.min(Math.abs(debtor.amount), creditor.amount);
      transactions.push({
        from: debtor.user,
        to: creditor.user,
        amount: Math.round(amount),
      });
      debtor.amount += amount;
      creditor.amount -= amount;
      if (Math.abs(debtor.amount) < 1) i++;
      if (creditor.amount < 1) j++;
    }
    return transactions;
  };

  const transactions = getTransactions();

  return (
    <div className="mx-4 space-y-6">
      <div className="bg-starbucks-dark text-white p-6 rounded-3xl shadow-lg relative overflow-hidden">
        {/* ... */}
        <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
          <CheckCircle2 className="text-gold" /> 結算建議 (台幣 NT$)
        </h3>
        {transactions.length === 0 ? (
          <p className="text-center text-white/60 py-4">目前帳目已平 🎉</p>
        ) : (
          <div className="space-y-3">
            {transactions.map((t, idx) => (
              <div
                key={idx}
                className="flex justify-between items-center bg-white/10 p-3 rounded-xl backdrop-blur-sm"
              >
                <div className="flex items-center gap-2">
                  <span className="font-bold text-red-300">{t.from}</span>
                  <span className="text-xs text-white/60">給</span>
                  <span className="font-bold text-green-300">{t.to}</span>
                </div>
                {/* 這裡顯示 NT$ */}
                <span className="font-mono font-bold text-xl text-gold">
                  NT$ {t.amount.toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="grid gap-3 pb-8">
        <h3 className="text-sm font-bold text-hojicha uppercase ml-2 tracking-widest">
          詳細收支表 (NT$)
        </h3>
        {ALL_USERS.map((user) => {
          const bal = Math.round(balances[user]);
          const isPositive = bal > 0;
          return (
            <div
              key={user}
              className="bg-white p-4 rounded-2xl border border-hojicha-light/20 shadow-sm flex justify-between items-center"
            >
              <div>
                <span className="font-bold text-starbucks-dark text-lg">
                  {user}
                </span>
                <p className="text-xs text-gray-400 mt-1">
                  先墊: NT${Math.round(details[user].paid).toLocaleString()} /
                  應付: NT$
                  {Math.round(details[user].shouldPay).toLocaleString()}
                </p>
              </div>
              <div
                className={`text-right ${isPositive ? "text-green-600" : bal < 0 ? "text-red-500" : "text-gray-400"}`}
              >
                <span className="text-xs font-bold block">
                  {isPositive ? "應收" : bal < 0 ? "應付" : "平"}
                </span>
                <span className="font-mono font-bold text-xl">
                  {isPositive ? "+" : ""} {bal.toLocaleString()}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
